import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { computeLeaveDays } from '@lemoment/shared';
import type {
  CreateLeaveRequest,
  Leave,
  LeaveListResponse,
  MeResponse,
  UpdateLeaveRequest,
} from '@lemoment/shared';

import { cancelLeave, createLeave, updateLeave } from '../apis';
import { leaveKeys } from './leaveKeys';

type ListSnapshot = [readonly unknown[], LeaveListResponse | undefined][];

/** Snapshot every cached leave list so a failed mutation can roll back. */
const snapshotLists = (qc: QueryClient): ListSnapshot =>
  qc.getQueriesData<LeaveListResponse>({ queryKey: leaveKeys.lists() });

const restoreLists = (qc: QueryClient, prev?: ListSnapshot) =>
  prev?.forEach(([key, data]) => qc.setQueryData(key, data));

/** Map over the leaves in every cached list (calendar + my-leaves share it). */
const patchLists = (qc: QueryClient, map: (leaves: Leave[]) => Leave[]) =>
  qc.setQueriesData<LeaveListResponse>(
    { queryKey: leaveKeys.lists() },
    (old) => (old ? { ...old, leaves: map(old.leaves) } : old),
  );

const resolveEndDate = (
  type: Leave['type'],
  startDate: string,
  endDate: string,
) => (type === 'annual' ? endDate : startDate);

/** Invalidate every query a leave change can affect, to reconcile with server. */
const useInvalidateLeaves = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: leaveKeys.all });
    qc.invalidateQueries({ queryKey: leaveKeys.me() });
    // Team views (today-away) live under the ['team', ...] namespace.
    qc.invalidateQueries({ queryKey: ['team'] });
  };
};

export const useCreateLeave = () => {
  const qc = useQueryClient();
  const invalidate = useInvalidateLeaves();
  return useMutation({
    mutationFn: (body: CreateLeaveRequest) => createLeave(body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: leaveKeys.lists() });
      const prev = snapshotLists(qc);
      const me = qc.getQueryData<MeResponse>(leaveKeys.me());
      const startDate = body.startDate;
      const endDate = resolveEndDate(
        body.type,
        startDate,
        body.endDate ?? startDate,
      );
      const optimistic: Leave = {
        id: `optimistic-${crypto.randomUUID()}`,
        crewId: me?.crew.id ?? '',
        type: body.type,
        startDate,
        endDate,
        days: computeLeaveDays(body.type, startDate, endDate),
        reason: body.reason,
        status: 'active',
        createdAt: new Date().toISOString(),
      };
      patchLists(qc, (leaves) => [optimistic, ...leaves]);
      return { prev };
    },
    onError: (_e, _body, ctx) => restoreLists(qc, ctx?.prev),
    onSettled: invalidate,
  });
};

export const useUpdateLeave = () => {
  const qc = useQueryClient();
  const invalidate = useInvalidateLeaves();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLeaveRequest }) =>
      updateLeave(id, body),
    onMutate: async ({ id, body }) => {
      await qc.cancelQueries({ queryKey: leaveKeys.lists() });
      const prev = snapshotLists(qc);
      patchLists(qc, (leaves) =>
        leaves.map((leave) => {
          if (leave.id !== id) {
            return leave;
          }
          const type = body.type ?? leave.type;
          const startDate = body.startDate ?? leave.startDate;
          const endDate = resolveEndDate(
            type,
            startDate,
            body.endDate ?? leave.endDate,
          );
          return {
            ...leave,
            type,
            startDate,
            endDate,
            days: computeLeaveDays(type, startDate, endDate),
            reason: body.reason ?? leave.reason,
          };
        }),
      );
      return { prev };
    },
    onError: (_e, _vars, ctx) => restoreLists(qc, ctx?.prev),
    onSettled: invalidate,
  });
};

export const useCancelLeave = () => {
  const qc = useQueryClient();
  const invalidate = useInvalidateLeaves();
  return useMutation({
    mutationFn: (id: string) => cancelLeave(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: leaveKeys.lists() });
      const prev = snapshotLists(qc);
      patchLists(qc, (leaves) =>
        leaves.map((leave) =>
          leave.id === id
            ? {
                ...leave,
                status: 'canceled',
                canceledAt: new Date().toISOString(),
              }
            : leave,
        ),
      );
      return { prev };
    },
    onError: (_e, _id, ctx) => restoreLists(qc, ctx?.prev),
    onSettled: invalidate,
  });
};
